/**
 * AddNewModal - Shared modal component for adding new items
 * Provides a consistent UI for create/add operations across admin sections
 * Uses ModalContainer for consistent modal structure
 */
import { useState } from 'react';
import ModalContainer from './ModalContainer';

export default function AddNewModal({
  isOpen,
  onClose,
  onSubmit,
  title = 'Add New',
  submitLabel = 'Create',
  fields = [],
  isSubmitting = false
}) {
  const [formData, setFormData] = useState(() => {
    const initialData = {};
    fields.forEach(field => {
      initialData[field.name] = field.defaultValue || '';
    });
    return initialData;
  });

  const [errors, setErrors] = useState({});

  const handleChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    // Clear error for this field when user starts typing
    if (errors[fieldName]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    fields.forEach(field => {
      if (field.required && !formData[field.name]) {
        newErrors[field.name] = `${field.label} is required`;
      }
      if (field.validate) {
        const error = field.validate(formData[field.name]);
        if (error) {
          newErrors[field.name] = error;
        }
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      await onSubmit(formData);
    }
  };

  const handleClose = () => {
    setFormData(() => {
      const initialData = {};
      fields.forEach(field => {
        initialData[field.name] = field.defaultValue || '';
      });
      return initialData;
    });
    setErrors({});
    onClose();
  };

  return (
    <ModalContainer
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      isProcessing={isSubmitting}
      footer={
        <div className="flex justify-end space-x-3 p-6">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            style={{ color: 'var(--color-text-on-accent)' }}
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Creating...</span>
              </>
            ) : (
              <span>{submitLabel}</span>
            )}
          </button>
        </div>
      }
    >
      {/* Form Content */}
      <div className="p-6 space-y-6">
        {fields.map((field) => (
          <div key={field.name}>
            <label className="text-sm font-semibold text-gray-200 mb-2 block">
              {field.label}
              {field.required && <span className="text-red-400 ml-1">*</span>}
            </label>

            {field.type === 'textarea' ? (
              <textarea
                value={formData[field.name]}
                onChange={(e) => handleChange(field.name, e.target.value)}
                placeholder={field.placeholder}
                rows={field.rows || 4}
                disabled={isSubmitting}
                className={`w-full px-4 py-2 bg-gray-900/50 border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed ${
                  errors[field.name] ? 'border-red-500' : 'border-gray-700'
                }`}
              />
            ) : field.type === 'select' ? (
              <select
                value={formData[field.name]}
                onChange={(e) => handleChange(field.name, e.target.value)}
                disabled={isSubmitting}
                className={`w-full px-4 py-2 bg-gray-900/50 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed ${
                  errors[field.name] ? 'border-red-500' : 'border-gray-700'
                }`}
              >
                <option value="">{field.placeholder || 'Select...'}</option>
                {field.options?.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : field.type === 'checkbox' ? (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData[field.name]}
                  onChange={(e) => handleChange(field.name, e.target.checked)}
                  disabled={isSubmitting}
                  className="w-4 h-4 bg-gray-900/50 border border-gray-700 rounded focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                />
                {field.description && (
                  <span className="ml-2 text-sm text-gray-400">{field.description}</span>
                )}
              </div>
            ) : (
              <input
                type={field.type || 'text'}
                value={formData[field.name]}
                onChange={(e) => handleChange(field.name, e.target.value)}
                placeholder={field.placeholder}
                disabled={isSubmitting}
                className={`w-full px-4 py-2 bg-gray-900/50 border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed ${
                  errors[field.name] ? 'border-red-500' : 'border-gray-700'
                }`}
              />
            )}

            {field.description && field.type !== 'checkbox' && (
              <p className="text-sm text-gray-400 mt-1">{field.description}</p>
            )}

            {errors[field.name] && (
              <p className="text-sm text-red-400 mt-1">{errors[field.name]}</p>
            )}
          </div>
        ))}
      </div>
    </ModalContainer>
  );
}
